import React, { type JSX } from "react";

import { useMountEffect } from "../../../hooks/useMountEffect";
import * as signals from "../../../store/signals";
import { getLongestFinancingPlan } from "../../../utils/financing";
import { IPaymentMethodFormProps } from "../PaymentMethod.interfaces";
import { AddAccountForm } from "../PaymentMethodFinancingAddAccountForm";
import { FinancingDisabledWarning } from "../PaymentMethodFinancingDisabled";
import { IPayment } from "../reducers.interfaces";
import { IFinancingPayment } from "../reducers.interfaces";
import { getFinancingData, isFortivaAcctNum } from "../utils";

interface IProps extends IPaymentMethodFormProps {
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
    buildFortivaFinancingUpsell?: (grandTotal: string) => JSX.Element | null;
    setPaymentMethodFields: (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => void;
    resetPaymentMethods: (methods: readonly IPayment["method_type"][]) => void;
    changeFinancingAccountNumber: (
        methodKey: string,
        accountNumber: string,
    ) => void;
    changeAmount: (methodKey: string, amount: string) => void;
    onApplyDenial: () => void;
    addSplitPayToFinancing: () => void;
    enableSplitPay: boolean;
}

const getMethodData = (props: IProps): IFinancingPayment => {
    const data = props.form.payment_methods[props.methodKey];
    let wellsFargoData: IFinancingPayment | null = null;
    if (data.method_type === "financing") {
        wellsFargoData = data;
    }
    const baseFields: IFinancingPayment = {
        method_type: "financing",
        amount: "",
        pay_balance: false,
        new_financing_account: null,
        financing_account: "",
        financing_plan: null,
        has_agreed: false,
        has_esigned: false,
    };
    return {
        ...baseFields,
        ...wellsFargoData,
    };
};

const buildFinancingCTA = (props: IProps): JSX.Element | null => {
    if (!props.data.basket || !props.derived.grand_total) {
        return null;
    }
    const methodData = getMethodData(props);
    const switchToCreditCard = () => {
        props.resetPaymentMethods(props.data.payment_methods);
    };
    // Figure out if credit applications are allowed
    const allowCreditApplications = props.data.financing_plans.reduce<boolean>(
        (memo, plan) => {
            return memo || plan.allow_credit_application;
        },
        false,
    );

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        props.onValidStateChange(fieldName, errorMessages, props.methodKey);
    };

    // Add a pre-existing Wells Fargo account by it's account number
    return (
        <div className="financing-form__cta financing-form__cta--add-account">
            <AddAccountForm
                header={props.header}
                methodKey={props.methodKey}
                countries={props.data.countries}
                billing_states={props.data.billing_states}
                savedAddresses={props.data.addresses}
                form={props.form}
                errors={props.errors}
                setPaymentMethodFields={props.setPaymentMethodFields}
                changeFinancingAccountNumber={
                    props.changeFinancingAccountNumber
                }
                changeAmount={props.changeAmount}
                onValidStateChange={onValidStateChange}
                showErrorMessages={props.showErrorMessages}
                plans={props.data.financing_plans}
                grandTotal={props.derived.grand_total}
                allowCreditApplications={allowCreditApplications}
                switchToCreditCard={switchToCreditCard}
                newAccount={methodData.new_financing_account || undefined}
                basketTotal={parseFloat(props.derived.grand_total)}
                cartID={props.data.basket.encoded_basket_id}
                isSplitPay={props.isSplitPay}
                shouldFreezeAmount={props.shouldFreezeAmount}
                disabled={props.disabled}
                derived={props.derived}
                addSplitPayToFinancing={props.addSplitPayToFinancing}
                enableSplitPay={props.enableSplitPay}
            />
        </div>
    );
};

const buildFinancingBlock = (props: IProps): JSX.Element => {
    let upsell: JSX.Element | null = null;
    const data = getFinancingData(props);
    if (props.derived.grand_total) {
        // Display Fortiva Upsell If Fortiva Account has authenticated
        if (
            props.buildFortivaFinancingUpsell &&
            data &&
            isFortivaAcctNum(data.financing_account)
        ) {
            upsell = props.buildFortivaFinancingUpsell(
                props.derived.grand_total,
            );
        } else {
            upsell = props.buildFinancingUpsell(props.derived.grand_total);
        }
    }
    return (
        <div className="financing-form">
            {upsell}
            {buildFinancingCTA(props)}
        </div>
    );
};

export const Financing = (props: IProps): React.ReactNode => {
    useMountEffect(() => {
        const handler_onApproval = signals.financing.onApproval.on(
            (account) => {
                // Select the new account. Set has_agreed and has_esigned
                // since the customer already consented during the WF application.
                props.setPaymentMethodFields(props.methodKey, {
                    new_financing_account: account,
                    financing_account: account.account_number,
                    has_agreed: true,
                    has_esigned: true,
                });
            },
        );
        const handler_onDenial = signals.financing.onDenial.on(() => {
            props.onApplyDenial();
        });

        return () => {
            handler_onApproval.dispose();
            handler_onDenial.dispose();
        };
    });

    // Financing is disabled if no financing plans were returned
    const longestActivePlan = getLongestFinancingPlan(
        props.data.financing_plans,
    );
    // Financing might be completely disabled
    if (!longestActivePlan) {
        return (
            <div className="checkout-step__payment-form">
                <FinancingDisabledWarning />
            </div>
        );
    }
    // Standard behavior for when financing is enabled
    return (
        <div className="checkout-step__payment-form">
            {buildFinancingBlock(props)}
        </div>
    );
};
